<template>
  <!-- ══ Comment Dialog (add / edit) ══ -->
  <v-dialog :value="value" max-width="560" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon left color="primary">mdi-comment-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">
            {{ editingComment ? 'Edit comment' : 'Add comment' }}
          </span>
        </div>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <v-form ref="commentForm">
          <!-- Type selector — only shown when creating a new comment -->
          <v-select
            v-if="!editingComment"
            v-model="form.type"
            :items="commentTypeOptions"
            label="Comment type"
            dense outlined
            class="mb-3"
            :rules="[v => !!v || 'Required']"
          />

          <!-- Message body -->
          <v-textarea
            v-model="form.message"
            label="Comment *"
            rows="4"
            dense outlined
            :rules="[v => !!v || 'Required']"
            autofocus
          />

          <!-- Evidence URLs (new comment only) -->
          <template v-if="!editingComment && supportsEvidenceUrls">
            <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">
              Evidence URLs <span class="font-weight-regular">(optional)</span>
            </div>
            <div
              v-for="(url, i) in form.evidenceUrls"
              :key="i"
              class="d-flex align-center mb-1"
              style="gap:4px"
            >
              <v-text-field
                v-model="form.evidenceUrls[i]"
                :label="`URL ${i + 1}`"
                dense outlined hide-details
                prepend-inner-icon="mdi-link"
              />
              <v-btn icon small @click="removeEvidenceUrl(i)">
                <v-icon small color="error">mdi-close-circle-outline</v-icon>
              </v-btn>
            </div>
            <v-btn x-small text color="primary" class="mt-1" @click="addEvidenceUrl">
              <v-icon x-small left>mdi-plus</v-icon>Add URL
            </v-btn>
          </template>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="saving"
          @click="submit"
        >
          {{ editingComment ? 'Save changes' : 'Add comment' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AmlCommentDialog',

  props: {
    value: {
      type:    Boolean,
      default: false,
    },
    editingComment: {
      type:    Object,
      default: null,
    },
    saving: {
      type:    Boolean,
      default: false,
    },
    supportsEvidenceUrls: {
      type:    Boolean,
      default: true,
    },
  },

  data() {
    return {
      form: this.buildForm(),

      commentTypeOptions: [
        'NOTE',
        'FINDING',
        'RECOMMENDATION',
        'ESCALATION',
        'FOLLOW_UP',
      ],
    };
  },

  watch: {
    /**
     * Re-initialise the form whenever the dialog opens or the editing target
     * changes (e.g. user opens edit on a different comment without closing first).
     */
    value(open) {
      if (open) this.form = this.buildForm();
    },
    editingComment() {
      this.form = this.buildForm();
    },
  },

  methods: {
    buildForm() {
      if (this.editingComment) {
        return {
          type:         this.editingComment.type,
          message:      this.editingComment.message,
          evidenceUrls: [...(this.editingComment.evidenceUrls ?? [])],
        };
      }
      return { type: 'NOTE', message: '', evidenceUrls: [] };
    },

    addEvidenceUrl() {
      this.form.evidenceUrls.push('');
    },

    removeEvidenceUrl(index) {
      this.form.evidenceUrls.splice(index, 1);
    },

    submit() {
      if (!this.$refs.commentForm.validate()) return;
      const payload = { ...this.form };
      // Strip empty evidence URLs before handing off to parent
      payload.evidenceUrls = payload.evidenceUrls.filter(u => u.trim());
      this.$emit('submit', payload);
    },

    close() {
      this.$refs.commentForm?.reset();
      this.$emit('input', false);
      this.$emit('close');
    },
  },
};
</script>